import React, { useState } from "react";
import {
  MdOutlineSearch,
  MdFilterAlt,
  MdOutlineFolderOpen,
  MdMoreVert,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AccountDetails from "../admin/pages/AccountDetails";
// import { useAccount } from '../context/AccountContext';
import { useSelector } from "react-redux";

// Compute days remaining based on subscription type:
//   is_free_trial === 1  → use trial_ends_at   (free trial countdown)
//   is_free_trial === 0  → use subs_end_at      (paid subscription countdown)
// Returns { days, isTrial } or null if no relevant subscription found.
const getSubscriptionInfo = () => {
  try {
    const raw = localStorage.getItem("subscription");
    if (!raw) return null;
    const sub = JSON.parse(raw);
    if (!sub) return null;

    if (sub.is_free_trial === 1) {
      // Rule 1: free trial — count down to trial_ends_at
      if (!sub.trial_ends_at) return null;
      const ends = new Date(sub.trial_ends_at);
      const now  = new Date();
      const diff = Math.ceil((ends - now) / (1000 * 60 * 60 * 24));
      return { days: diff > 0 ? diff : 0, isTrial: true, totalDays: 30 };
    } else {
      // Rule 2: paid subscription — count down to subs_end_at
      if (!sub.subs_end_at) return null;
      const ends = new Date(sub.subs_end_at);
      const now  = new Date();
      const diff = Math.ceil((ends - now) / (1000 * 60 * 60 * 24));
      return { days: diff > 0 ? diff : 0, isTrial: false, totalDays: 30 };
    }
  } catch (_) {
    return null;
  }
};

const Header = ({
  onToggleRules,
  isRulesActive,
  onNavigateToDrivers,
  onSearch,
}) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { accountId } = useSelector((state) => state.account);
  const [activeButton, setActiveButton] = useState("myDrivers");
  const [searchItem, setSearchItem] = useState("");
  const trialInfo = getSubscriptionInfo();
  console.log("header call: ", user);

  // const { accountId } = useAccount();
  console.log("🚀 Header component rendered with account_id:", accountId);

  const handleFilterClick = () => {
    if (onToggleRules) onToggleRules();
  };

  const handleSearchChange = (event) => {
    const searchInputItem = event.target.value;
    setSearchItem(searchInputItem);
    if (onSearch) {
      console.log("handleSearchChange:onSearch>", searchItem);
      onSearch(searchInputItem);
    }
  };

  return (
    // Remove fixed positioning - let it be part of the normal flow
    <div className="w-full h-full flex items-center justify-between px-6 bg-white border-b border-gray-200">
      {/* Left: Search Bar */}
      <div className="relative w-64">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MdOutlineSearch className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search..."
          className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-gray-50/70"
          value={searchItem}
          onChange={handleSearchChange}
        />
      </div>

      {/* Center: Mini AccountDetails */}
      <div className="flex-1 flex justify-center px-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-2 hover:shadow-md transition-shadow duration-200">
          <AccountDetails accountId={accountId} mini={true} />
        </div>
      </div>

      {/* Right: Icon Actions */}
      <div className="flex items-center space-x-5 text-gray-600">

        {/* Trial / Subscription Badge */}
        {trialInfo !== null && (() => {
          const { days, isTrial, totalDays } = trialInfo;
          const isExpired = days === 0;
          const isUrgent  = days <= 5;
          const isWarning = days <= 10 && days > 5;
          const pct  = Math.max(0, Math.min(100, (days / totalDays) * 100));
          const r    = 10;
          const circ = 2 * Math.PI * r;
          const dash = (pct / 100) * circ;

          // bg gradient + glow color
          // trial: purple→blue  |  paid: green  |  urgent/expired: red
          const bg = isExpired || isUrgent
            ? { grad: "linear-gradient(135deg,#ff416c,#ff4b2b)", glow: "rgba(255,65,108,0.45)" }
            : isWarning
            ? { grad: "linear-gradient(135deg,#f7971e,#ffd200)", glow: "rgba(247,151,30,0.45)" }
            : isTrial
            ? { grad: "linear-gradient(135deg,#6a11cb,#2575fc)", glow: "rgba(106,17,203,0.35)" }
            : { grad: "linear-gradient(135deg,#11998e,#38ef7d)", glow: "rgba(17,153,142,0.40)" };

          // ring track colour
          const trackColor = "rgba(255,255,255,0.25)";

          // number accent colour
          const numColor = isWarning ? "#fff176" : "#ffffff";

          // label text
          const labelText = isExpired
            ? (isTrial ? "Trial Expired" : "Plan Expired")
            : (isTrial ? "Free Trial" : "Subscription");

          // tooltip text
          const tooltipText = isExpired
            ? (isTrial ? "Your free trial has expired" : "Your subscription has expired")
            : `${isTrial ? "Free trial" : "Subscription"} ends in ${days} day${days !== 1 ? "s" : ""}`;

          return (
            <div
              style={{
                background: bg.grad,
                boxShadow: `0 4px 18px ${bg.glow}`,
              }}
              className="relative flex items-center gap-2.5 pl-2.5 pr-4 py-1.5 rounded-full cursor-default select-none"
              title={tooltipText}
            >
              {/* Pulsing dot */}
              {(isUrgent || isExpired) && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-300"></span>
                </span>
              )}

              {/* Circular progress ring */}
              <svg width="28" height="28" className="flex-shrink-0 -rotate-90" viewBox="0 0 26 26">
                <circle cx="13" cy="13" r={r} fill="none" stroke={trackColor} strokeWidth="2.5" />
                <circle
                  cx="13" cy="13" r={r}
                  fill="none"
                  stroke="rgba(255,255,255,0.95)"
                  strokeWidth="2.5"
                  strokeDasharray={`${dash} ${circ}`}
                  strokeLinecap="round"
                />
              </svg>

              {/* Text */}
              <div className="flex flex-col leading-none gap-0.5">
                <span className="text-[9px] font-semibold text-white/80 uppercase tracking-widest whitespace-nowrap">
                  {labelText}
                </span>
                {!isExpired && (
                  <span className="whitespace-nowrap leading-none" style={{ color: numColor }}>
                    <span className="text-[15px] font-extrabold">{days}</span>
                    <span className="text-[10px] font-semibold text-white/80"> day{days !== 1 ? "s" : ""} left</span>
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        <ActionButton
          onClick={() => {
            setActiveButton("myDrivers");
            console.log("Navigating to DriversPage");
            onNavigateToDrivers && onNavigateToDrivers();
            navigate("?view=drivers");
          }}
          icon={<MdOutlineFolderOpen size={20} />}
          label="My Drivers"
          tooltip="View Drivers"
          isActive={activeButton === "myDrivers"}
        />

        <ActionButton
          onClick={() => {
            setActiveButton("myFilters");
            handleFilterClick();
          }}
          icon={<MdFilterAlt size={20} />}
          label={isRulesActive ? "Close Filters" : "My Filters"}
          tooltip="Manage Filters"
          isActive={activeButton === "myFilters"}
        />

        <ActionButton
          onClick={() => setActiveButton("myDocs")}
          icon={<MdOutlineFolderOpen size={20} />}
          label="My Docs"
          tooltip="Open Documentation"
          isActive={activeButton === "myDocs"}
        />

        <ActionButton
          icon={<MdMoreVert size={20} />}
          label="More"
          tooltip="More Options"
        />

      </div>
    </div>
  );
};

// Reusable Action Button Component
const ActionButton = ({ onClick, icon, label, tooltip, isActive = false }) => (
  <button
    onClick={onClick}
    className={`group flex flex-col items-center transition-all duration-200 ${
      isActive ? "text-blue-600" : "text-gray-600 hover:text-blue-600"
    }`}
    title={tooltip}
  >
    <div className="transform transition-transform group-hover:scale-105">
      {icon}
    </div>
    <span className="text-[10px] mt-0.5 font-medium">{label}</span>
  </button>
);

export default Header;
